"use client"

import { ChevronDownIcon } from "@heroicons/react/20/solid"
import { useIsActiveLink } from "@src/hooks/useIsActiveLink"
import {
  ContactsIcon,
  DealsIcon,
  EarnIcon,
  EarnSolidIcon,
  HistoryIcon,
  HomeIcon,
  HomeSolidIcon,
  SwapSquareIcon,
  SwapSquareSolidIcon,
  TradeIcon,
  TradeSolidIcon,
  WalletIcon,
  WalletSolidIcon,
} from "@src/icons"
import { useActivityDock } from "@src/providers/ActivityDockProvider"
import type { FeatureFlagValues } from "@src/providers/FeatureFlagsProvider"
import { FeatureFlagsContext } from "@src/providers/FeatureFlagsProvider"
import { clsx } from "clsx"
import { AnimatePresence, LayoutGroup, motion } from "framer-motion"
import Link from "next/link"
import { type ComponentType, useContext, useState } from "react"
import { DockCard } from "../DockCard"

type NavItemBase = {
  label: string
  icon: ComponentType<React.SVGProps<SVGSVGElement>>
  iconSolid?: ComponentType<React.SVGProps<SVGSVGElement>>
  shortLabel?: string
}

export type NavLinkItemType = NavItemBase & {
  href: string
  children?: never
  showInTabs?: boolean
}

export type NavGroupItemType = NavItemBase & {
  href?: never
  children: NavLinkItemType[]
  showInTabs?: false
}

export type NavItemType = NavLinkItemType | NavGroupItemType

export const navItems: NavItemType[] = [
  {
    label: "Home",
    href: "/home",
    icon: HomeIcon,
    iconSolid: HomeSolidIcon,
    showInTabs: true,
  },
  {
    label: "Assets",
    href: "/crypto",
    icon: WalletIcon,
    iconSolid: WalletSolidIcon,
    showInTabs: true,
  },
  {
    label: "Trade",
    icon: TradeIcon,
    children: [
      {
        label: "Swap",
        href: "/swap",
        icon: SwapSquareIcon,
        iconSolid: SwapSquareSolidIcon,
        showInTabs: true,
      },
      {
        label: "Perps",
        href: "/perps",
        icon: TradeIcon,
        iconSolid: TradeSolidIcon,
        showInTabs: true,
      },
    ],
  },
  {
    label: "Earn",
    href: "/earn",
    icon: EarnIcon,
    iconSolid: EarnSolidIcon,
  },
  {
    label: "Private deals",
    shortLabel: "Deals",
    href: "/deals",
    icon: DealsIcon,
  },
  {
    label: "History",
    href: "/history",
    icon: HistoryIcon,
  },
  {
    label: "Contacts",
    href: "/contacts",
    icon: ContactsIcon,
  },
]

export function isNavLinkItem(item: NavItemType): item is NavLinkItemType {
  return "href" in item
}

const disabledRoutes: Record<string, keyof FeatureFlagValues> = {
  "/swap": "isSwapDisabled",
  "/receive": "isDepositsDisabled",
  "/send": "isWithdrawDisabled",
  "/send/confidential": "isWithdrawDisabled",
  "/deals": "isDealsDisabled",
  "/earn": "isEarnDisabled",
  "/perps": "isTradeDisabled",
}

export function useVisibleNavItems(): NavItemType[] {
  const flags = useContext(FeatureFlagsContext)

  const isEnabled = (item: NavLinkItemType) => {
    const key = disabledRoutes[item.href]
    return !key || !flags[key]
  }

  return navItems.flatMap<NavItemType>((item) => {
    if (isNavLinkItem(item)) {
      return isEnabled(item) ? [item] : []
    }

    const children = item.children.filter(isEnabled)
    return children.length > 0 ? [{ ...item, children }] : []
  })
}

export function NavbarDesktop() {
  const { dockOpen, dockItems } = useActivityDock()
  const visibleItems = useVisibleNavItems()

  return (
    <div
      className={clsx(
        "relative flex flex-1 flex-col overflow-hidden border-gray-300 confidential:border-white/10 border-y transition-colors duration-200",
        dockOpen && "bg-gray-200 confidential:bg-gray-700"
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {dockOpen ? (
          <motion.div
            key="dock"
            initial={{ x: "-110%" }}
            animate={{ x: 0 }}
            exit={{ x: "-110%" }}
            transition={{ type: "spring", stiffness: 500, damping: 40 }}
            className="hide-scrollbar relative z-10 flex-1 overflow-y-auto overscroll-none p-4"
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {dockItems.toReversed().map((item, index) => (
                <DockCard key={item.id} item={item} isFirst={index === 0} />
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.nav
            key="nav"
            initial={{ x: "-110%" }}
            animate={{ x: 0 }}
            exit={{ x: "-110%" }}
            transition={{ type: "spring", stiffness: 500, damping: 40 }}
            className="space-y-1 overflow-y-auto p-4"
          >
            <LayoutGroup id="desktop-nav">
              {visibleItems.map((item) => (
                <NavItem key={item.label} {...item} />
              ))}
            </LayoutGroup>
          </motion.nav>
        )}
      </AnimatePresence>
    </div>
  )
}

function NavItem(item: NavItemType & { isChild?: boolean }) {
  const { isActive } = useIsActiveLink()
  const { label: labelText, icon: Icon } = item

  const hasActiveChild =
    !isNavLinkItem(item) &&
    item.children.some((child) => isActive(child.href, false))

  const [isOpen, setIsOpen] = useState(hasActiveChild)
  const toggleOpen = () => setIsOpen((prev) => !prev)

  const classes =
    "flex w-full items-center gap-4 rounded-2xl p-3.5 text-left confidential:text-gray-400 text-gray-500 confidential:hover:text-white hover:text-gray-700 confidential:hover:bg-white/7 hover:bg-gray-900/6"

  const icon = (
    <span className="flex w-5.5 shrink-0 items-center justify-center">
      <Icon className="size-5.5" />
    </span>
  )

  const label = (
    <span className="flex-1 font-semibold text-base/5.5">{labelText}</span>
  )

  if (!isNavLinkItem(item)) {
    return (
      <div>
        <button type="button" onClick={toggleOpen} className={classes}>
          {icon}
          {label}
          <ChevronDownIcon
            className={clsx(
              "size-5 shrink-0 transition-transform duration-150",
              isOpen && "rotate-180"
            )}
          />
        </button>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: "spring", bounce: 0, duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex pt-1">
                <div className="mr-0.5 ml-3.5 flex w-5.5 items-center justify-center">
                  <div className="h-full w-0.5 rounded-full bg-gray-300 confidential:bg-gray-700" />
                </div>

                <div className="flex flex-1 flex-col gap-1">
                  {item.children.map((child) => (
                    <NavItem key={child.label} {...child} isChild />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  const { href, isChild } = item

  return (
    <Link
      href={href}
      className={clsx(classes, {
        "bg-gray-900/6 confidential:bg-white/7 confidential:text-white text-gray-700":
          isActive(href),
      })}
    >
      {!isChild && icon}
      {label}
    </Link>
  )
}
